import { theme, ThemeConfig } from 'antd';

import { WebApp } from 'shared/utils';

export const THEME_LIGHT = (): ThemeConfig => ({
	algorithm: theme.defaultAlgorithm,
	cssVar: true,

	token: {
		fontFamily: 'Roboto',
		colorSplit: WebApp?.themeParams.section_separator_color || 'rgba(5, 5, 5, 0.06)',
		colorPrimary: WebApp?.themeParams.accent_text_color || '#0078F0',
		colorLink: WebApp?.themeParams.accent_text_color || '#0078F0',

		colorBgLayout: WebApp?.themeParams.secondary_bg_color || '#f5f5f5',
		colorBgContainer: WebApp?.themeParams.bg_color || '#ffffff',
		colorTextHeading: WebApp?.themeParams.text_color || 'rgba(0, 0, 0, 0.88)',
		colorTextBase: WebApp?.themeParams.text_color || 'rgba(0, 0, 0, 0.88)',
		colorText: WebApp?.themeParams.text_color || 'rgba(0, 0, 0, 0.88)',
		colorTextSecondary: WebApp?.themeParams.subtitle_text_color || '#4f5153',
		colorTextDescription: WebApp?.themeParams.subtitle_text_color || '#4f5153',
		colorBgElevated: WebApp?.themeParams.bg_color || '#ffffff',
		colorBgBase: WebApp?.themeParams.bg_color || '#ffffff',
		colorTextTertiary: WebApp?.themeParams.hint_color || '#4F5153',

		controlHeightLG: 44,
		controlHeight: 36,
		controlHeightSM: 32,

		colorBorder: 'rgba(0, 0, 0, 0.1)',
		colorBorderSecondary: 'rgba(0, 0, 0, 0.05)',
		borderRadiusLG: 8,
		borderRadius: 8,

		lineHeight: 1.429,

		fontSizeLG: 14,
		fontSizeHeading4: 18,

		boxShadowTertiary: '0 0 0 2px rgba(5, 175, 255, 0.1)',
		colorPrimaryBorder: '#0078f0',
		colorPrimaryBorderHover: '#2899fc',
	},
	components: {
		Alert: {
			defaultPadding: 12,
			withDescriptionPadding: '8px 12px',
		},
		Badge: {
			fontSize: 12,
			colorText: WebApp?.themeParams.subtitle_text_color || '#4f5153',
		},
		Button: {
			contentFontSizeLG: 14,
			primaryShadow: 'none',
			colorBgContainer: WebApp?.themeParams.bg_color || '#ffffff',
			boxShadow: 'none',
			defaultShadow: 'none',
			boxShadowSecondary: 'none',
			boxShadowTertiary: 'none',
			dangerShadow: 'none',
			defaultBorderColor: 'rgba(0, 0, 0, 0.15)',
		},
		Checkbox: {
			colorBgContainer: WebApp?.themeParams.bg_color || '#ffffff',
			colorBorder: 'rgba(0, 0, 0, 0.15)',
		},
		Radio: {
			buttonBg: WebApp?.themeParams.bg_color || '#ffffff',
			buttonCheckedBg: WebApp?.themeParams.bg_color || '#ffffff',
			colorBorder: 'rgba(0, 0, 0, 0.15)',
		},
		Select: {
			selectorBg: WebApp?.themeParams.bg_color || '#ffffff',
			colorBorder: 'rgba(0, 0, 0, 0.15)',
		},
		Calendar: {
			colorBgContainer: WebApp?.themeParams.bg_color || '#ffffff',
			colorSplit: 'rgba(0, 0, 0, 0.15)',
			colorBorder: 'rgba(0, 0, 0, 0.15)',
		},
		Card: {
			bodyPadding: 12,
			boxShadow: 'none',
			boxShadowSecondary: 'none',
			boxShadowTertiary: 'none',
		},
		Form: {
			itemMarginBottom: 0,
		},
		Input: {
			colorBgContainer: WebApp?.themeParams.bg_color || '#ffffff',
			colorBorder: 'rgba(0, 0, 0, 0.15)',
			inputFontSizeLG: 14,
		},
		DatePicker: {
			colorBgContainer: WebApp?.themeParams.bg_color || '#ffffff',
			colorBorder: 'rgba(0, 0, 0, 0.15)',
		},
		Layout: {
			bodyBg: WebApp?.themeParams.secondary_bg_color || '#f5f5f5',
			colorBorderSecondary: WebApp?.themeParams.section_separator_color || '#f5f5f5',
		},
		Tag: {
			lineHeightSM: 1.5,
			marginXS: 4,
		},
	},
});

export const THEME_DARK = (): ThemeConfig => ({
	algorithm: theme.darkAlgorithm,
	cssVar: true,

	token: {
		fontFamily: 'Roboto',
		colorSplit: WebApp?.themeParams.section_separator_color || '#888c8f',
		colorPrimary: WebApp?.themeParams.accent_text_color || '#0078F0',
		colorLink: WebApp?.themeParams.accent_text_color || '#0078F0',

		colorInfoBorder: '#003060',
		colorWarningBorder: '#594214',

		colorBgLayout: WebApp?.themeParams.bg_color || '#141414',
		colorBgContainer: WebApp?.themeParams.secondary_bg_color || '#1F1F1F',
		colorTextHeading: WebApp?.themeParams.text_color || '#D0D1D2',
		colorTextBase: WebApp?.themeParams.text_color || '#D0D1D2',
		colorText: WebApp?.themeParams.text_color || '#D0D1D2',
		colorTextSecondary: WebApp?.themeParams.subtitle_text_color || '#828486',
		colorTextDescription: WebApp?.themeParams.subtitle_text_color || '#828486',
		colorBgElevated: WebApp?.themeParams.secondary_bg_color || '#1F1F1F',
		colorBgBase: WebApp?.themeParams.bg_color || '#141414',
		colorTextTertiary: WebApp?.themeParams.hint_color || '#4F5153',

		controlHeightLG: 44,
		controlHeight: 36,
		controlHeightSM: 32,

		colorBorder: 'rgba(255, 255, 255, 0.1)',
		colorBorderSecondary: 'rgba(255, 255, 255, 0.05)',
		borderRadiusLG: 8,
		borderRadius: 8,

		lineHeight: 1.429,

		fontSizeLG: 14,
		fontSizeHeading4: 18,

		boxShadowTertiary: '0 0 0 2px rgba(0, 66, 133, 0.54)',
		colorPrimaryBorder: '#0369cf',
		colorPrimaryBorderHover: '#268ce5',
	},
	components: {
		Alert: {
			defaultPadding: 12,
			withDescriptionPadding: '8px 12px',
		},
		Badge: {
			fontSize: 12,
			colorText: WebApp?.themeParams.subtitle_text_color || '#828486',
		},
		Button: {
			contentFontSizeLG: 14,
			primaryShadow: 'none',
			colorBgContainer: WebApp?.themeParams.bg_color || '#141414',
			boxShadow: 'none',
			defaultShadow: 'none',
			boxShadowSecondary: 'none',
			boxShadowTertiary: 'none',
			dangerShadow: 'none',
			defaultBorderColor: 'rgba(255, 255, 255, 0.15)',
		},
		Checkbox: {
			colorBgContainer: WebApp?.themeParams.bg_color || '#141414',
			colorBorder: 'rgba(255, 255, 255, 0.15)',
		},
		Radio: {
			buttonBg: WebApp?.themeParams.bg_color || '#141414',
			buttonCheckedBg: WebApp?.themeParams.bg_color || '#141414',
			colorBorder: 'rgba(255, 255, 255, 0.15)',
		},
		Select: {
			selectorBg: WebApp?.themeParams.bg_color || '#141414',
			colorBorder: 'rgba(255, 255, 255, 0.15)',
		},
		Calendar: {
			colorBgContainer: WebApp?.themeParams.bg_color || '#141414',
			colorSplit: 'rgba(255, 255, 255, 0.15)',
			colorBorder: 'rgba(255, 255, 255, 0.15)',
		},
		Card: {
			bodyPadding: 12,
			boxShadow: 'none',
			boxShadowSecondary: 'none',
			boxShadowTertiary: 'none',
		},
		Form: {
			itemMarginBottom: 0,
		},
		Input: {
			colorBgContainer: WebApp?.themeParams.bg_color || '#141414',
			colorBorder: 'rgba(255, 255, 255, 0.15)',
			inputFontSizeLG: 14,
		},
		DatePicker: {
			colorBgContainer: WebApp?.themeParams.bg_color || '#141414',
			colorBorder: 'rgba(255, 255, 255, 0.15)',
		},
		Layout: {
			bodyBg: WebApp?.themeParams.bg_color || '#141414',
			colorBorderSecondary: WebApp?.themeParams.section_separator_color || '#1F1F1F',
		},
		Tag: {
			lineHeightSM: 1.5,
			marginXS: 4,
		},
	},
});
